import { useMemo, useState } from "react";
import FormDropdown from "../../../../../components/common/FormDropdown";
import type { OfficialPick, OfficialsForm, OfficialSlot, SlotKey, TextKey } from "./matchOfficials";

const CLEAR = "__none__";

type Props = {
  value: OfficialsForm;
  onChange: (next: OfficialsForm) => void;
  umpires: OfficialPick[];
  scorers: OfficialPick[];
};

/**
 * A list-or-typed official. Picks come from the tournament's available umpires / scorers; umpires can instead be typed
 * (a name with no profile), like the mobile app. A pick is hidden from the other slots of the same kind.
 */
const SlotField = ({ id, label, slot, list, taken, allowManual, onChange }: {
  id: string; label: string; slot: OfficialSlot; list: OfficialPick[]; taken: string[]; allowManual: boolean; onChange: (s: OfficialSlot) => void;
}) => {
  const [typing, setTyping] = useState(() => !slot.pick && slot.manualName.length > 0);
  const manual = typing || (!slot.pick && slot.manualName.length > 0);
  const options = useMemo(() => {
    const free = list.filter((p) => !taken.includes(p.id) || p.id === slot.pick?.id);
    // keep a saved pick visible even if it is no longer in the available list
    const opts = slot.pick && !free.some((p) => p.id === slot.pick!.id) ? [slot.pick, ...free] : free;
    return [
      { value: "", label: "Select" },
      // the dropdown never offers an empty choice, so filled slots get an explicit way back to "none"
      ...(slot.pick ? [{ value: CLEAR, label: "None (clear)" }] : []),
      ...opts.map((p) => ({ value: p.id, label: p.name || p.id })),
    ];
  }, [list, taken, slot.pick]);

  return (
    <div className="schedule-match-field">
      <label htmlFor={id}>{label}</label>
      {manual ? (
        <>
          <input id={id} type="text" maxLength={80} placeholder="Official's name" value={slot.manualName} onChange={(e) => onChange({ pick: null, manualName: e.target.value })} />
          <button type="button" className="schedule-match-link" onClick={() => { setTyping(false); onChange({ pick: null, manualName: "" }); }}>Choose from list instead</button>
        </>
      ) : (
        <FormDropdown
          id={id}
          value={slot.pick?.id ?? ""}
          searchable
          searchPlaceholder="Search"
          options={options}
          placeholder="Select"
          onChange={(v) => {
            if (v === CLEAR) { onChange({ pick: null, manualName: "" }); return; }
            onChange({ pick: list.find((p) => p.id === v) ?? slot.pick ?? null, manualName: "" });
          }}
        />
      )}
      {!manual && allowManual && (
        <button type="button" className="schedule-match-link" onClick={() => { setTyping(true); onChange({ pick: null, manualName: "" }); }}>
          + Enter name manually
        </button>
      )}
    </div>
  );
};

const TextField = ({ id, label, placeholder, value, onChange }: { id: string; label: string; placeholder: string; value: string; onChange: (v: string) => void }) => (
  <div className="schedule-match-field">
    <label htmlFor={id}>{label}</label>
    <input id={id} type="text" maxLength={80} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

const MatchOfficialsFields = ({ value, onChange, umpires, scorers }: Props) => {
  const setSlot = (k: SlotKey) => (s: OfficialSlot) => onChange({ ...value, [k]: s });
  const setText = (k: TextKey) => (v: string) => onChange({ ...value, [k]: v });
  const umpireKeys: SlotKey[] = ["umpire1", "umpire2", "umpire3", "umpire4"];
  const takenBy = (k: SlotKey, keys: SlotKey[]) => keys.filter((o) => o !== k).map((o) => value[o].pick?.id).filter((x): x is string => !!x);
  const scorerKeys: SlotKey[] = ["scorer1", "scorer2"];

  return (
    <section className="schedule-match-officials" aria-label="Match officials">
      <h3>Match officials <span>All optional</span></h3>
      <div className="schedule-match-details-grid">
        <SlotField id="so-umpire-1" label="On-Field Umpire 1" slot={value.umpire1} list={umpires} taken={takenBy("umpire1", umpireKeys)} allowManual onChange={setSlot("umpire1")} />
        <SlotField id="so-umpire-2" label="On-Field Umpire 2" slot={value.umpire2} list={umpires} taken={takenBy("umpire2", umpireKeys)} allowManual onChange={setSlot("umpire2")} />
        <SlotField id="so-umpire-3" label="Third Umpire (Video)" slot={value.umpire3} list={umpires} taken={takenBy("umpire3", umpireKeys)} allowManual onChange={setSlot("umpire3")} />
        <SlotField id="so-umpire-4" label="Fourth Umpire (Support)" slot={value.umpire4} list={umpires} taken={takenBy("umpire4", umpireKeys)} allowManual onChange={setSlot("umpire4")} />
        <SlotField id="so-scorer-1" label="Scorer" slot={value.scorer1} list={scorers} taken={takenBy("scorer1", scorerKeys)} allowManual={false} onChange={setSlot("scorer1")} />
        <SlotField id="so-scorer-2" label="Scorer 2" slot={value.scorer2} list={scorers} taken={takenBy("scorer2", scorerKeys)} allowManual={false} onChange={setSlot("scorer2")} />
        <TextField id="so-commentator-1" label="Commentator 1" placeholder="Enter match commentator" value={value.commentator1} onChange={setText("commentator1")} />
        <TextField id="so-commentator-2" label="Commentator 2" placeholder="Enter match commentator" value={value.commentator2} onChange={setText("commentator2")} />
        <TextField id="so-referee" label="Match Referee" placeholder="Enter match referee name" value={value.referee} onChange={setText("referee")} />
        <TextField id="so-broadcaster" label="Broadcaster" placeholder="Enter broadcaster name" value={value.broadcaster} onChange={setText("broadcaster")} />
      </div>
    </section>
  );
};

export default MatchOfficialsFields;
